import { get } from 'lodash'
import { useEffect, useState } from 'react'

import { useLazyGetRouteStopsGeojsonQuery } from 'api/route/getRouteStops'
import { formatDateToBEFormatDateFns } from 'utils/date'

// Self-fetching variant of useRouteContainerFeatures for surfaces that don't
// already hold the route stops geojson (the bespoke edit / schedule maps).
// Fetches the geojson for the given routes + date and flattens the container
// `newContainers` features — the same payload + key the in-place hook reads.
// JSDoc so TS consumers aren't held to the empty-array default's `never[]`.
/**
 * @param {{ routeIds?: string[], date?: Date|null, enabled?: boolean }} params
 * @returns {object[]}
 */
export const useRouteContainerFeaturesForDate = ({ routeIds = [], date, enabled = true }) => {
  const [features, setFeatures] = useState([])
  const [getGeojson] = useLazyGetRouteStopsGeojsonQuery()

  const idsKey = routeIds.filter(Boolean).slice().sort().join(',')
  const beDate = date ? formatDateToBEFormatDateFns(date) : null

  useEffect(() => {
    const ids = idsKey ? idsKey.split(',') : []
    if (!enabled || ids.length === 0 || !beDate) {
      setFeatures([])
      return undefined
    }
    let cancelled = false
    const run = async () => {
      try {
        const data = await getGeojson({ routes: ids, serviceDate: beDate }).unwrap()
        const routeGeojsonData = get(data, 'routeGeojsonData', {})
        const next = ids.flatMap(id => get(routeGeojsonData, `${id}.newContainers.features`, []))
        if (!cancelled) setFeatures(next)
      } catch {
        if (!cancelled) setFeatures([])
      }
    }
    run()
    return () => {
      cancelled = true
    }
  }, [enabled, idsKey, beDate, getGeojson])

  return features
}

export default useRouteContainerFeaturesForDate
